import { useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { useLiveCities } from '@/features/destinations/hooks/useLiveCities';
import type { LiveCity } from '@/types';

interface LiveCitySelectProps {
  /** Selected city name; empty string means no city. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** `hero` renders light text for the dark landing hero. */
  variant?: 'hero' | 'page';
}

/**
 * Destination picker limited to the cities an admin has put live, so a search
 * never lands on a city with no guides.
 */
export function LiveCitySelect({
  value,
  onChange,
  placeholder = 'Where to?',
  variant = 'page',
}: LiveCitySelectProps) {
  const { cities, isLoading } = useLiveCities();
  const isHero = variant === 'hero';

  // A city from an old link or URL may no longer be live; keep it selectable
  // so the field still shows what is being searched.
  const options = useMemo<LiveCity[]>(() => {
    const trimmed = value.trim();
    if (!trimmed || cities.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
      return cities;
    }
    return [...cities, { _id: `current-${trimmed}`, name: trimmed }];
  }, [cities, value]);

  const selected =
    options.find((c) => c.name.toLowerCase() === value.trim().toLowerCase()) ?? null;

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(_event, city) => onChange(city?.name ?? '')}
      getOptionLabel={(city) => city.name}
      isOptionEqualToValue={(option, current) => option._id === current._id}
      loading={isLoading}
      autoHighlight
      fullWidth
      noOptionsText="No live city matches"
      filterOptions={(list, { inputValue }) => {
        const term = inputValue.trim().toLowerCase();
        if (!term) return list;
        return list.filter(
          (c) =>
            c.name.toLowerCase().includes(term) || c.region?.toLowerCase().includes(term)
        );
      }}
      renderOption={(props, city) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box component="li" key={key} {...rest} sx={{ py: 1, px: 1.75 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{city.name}</Typography>
              {city.region && (
                <Typography variant="caption" color="text.secondary">
                  {city.region}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <InputBase
          ref={params.InputProps.ref}
          inputProps={{ ...params.inputProps, 'aria-label': 'Destination' }}
          placeholder={placeholder}
          fullWidth
          endAdornment={
            isLoading ? (
              <CircularProgress
                size={16}
                sx={{ color: isHero ? 'rgba(255,255,255,0.7)' : 'primary.main' }}
              />
            ) : null
          }
          sx={{
            width: '100%',
            fontSize: isHero ? { xs: '0.875rem', md: '0.9375rem' } : '0.9375rem',
            fontWeight: value ? 600 : 400,
            color: isHero ? '#FFFFFF' : 'text.primary',
            '& input': {
              p: 0,
              color: 'inherit',
              fontWeight: 'inherit',
              '&::placeholder': {
                color: isHero ? 'rgba(255,255,255,0.55)' : 'text.muted',
                opacity: 1,
                fontWeight: 400,
              },
            },
          }}
        />
      )}
      slotProps={{
        paper: {
          elevation: 8,
          sx: { borderRadius: 2, border: '1px solid', borderColor: 'divider', mt: 1 },
        },
      }}
      sx={{ width: '100%' }}
    />
  );
}
